import type { FC } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import type { PrefetchHandlers } from '../config';

const styles = {
  sm: {
    root: 'size-8',
    icon: 'size-4',
  },
  md: {
    root: 'size-10',
    icon: 'size-5',
  },
  lg: {
    root: 'size-12',
    icon: 'size-6',
  },
};

interface NavItemDropdownButtonProps {
  /** Label for the dropdown trigger button (shown in aria-label). */
  label: string;
  /** Icon component for the trigger button. */
  icon: FC<{ className?: string }>;
  /** Whether any child item is currently active. */
  current?: boolean;
  /** Size of the trigger button. */
  size?: 'sm' | 'md' | 'lg';
  /** Placement of the tooltip. */
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  /** Child items to show in the dropdown. */
  items: Array<{
    label: string;
    href: string;
    icon?: FC<{ className?: string }>;
    onPrefetch?: PrefetchHandlers;
  }>;
}

export const NavItemDropdownButton = ({
  label,
  icon: Icon,
  current = false,
  size = 'md',
  tooltipPlacement = 'right',
  items,
}: NavItemDropdownButtonProps) => {
  return (
    <Dropdown.Root>
      <Tooltip title={label} placement={tooltipPlacement}>
        <AriaButton
          aria-label={label}
          className={_state =>
            cx(
              'relative flex w-full cursor-pointer items-center justify-center rounded-md bg-primary p-2 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
              current &&
                'bg-active text-fg-quaternary_hover hover:bg-secondary_hover',
              styles[size].root
            )
          }
        >
          <Icon
            aria-hidden='true'
            className={cx('shrink-0 transition-inherit-all', styles[size].icon)}
          />
        </AriaButton>
      </Tooltip>
      <Dropdown.Popover placement='right top'>
        {/* Items here are actions (real href links), not radio selections — override the
            Dropdown.Menu default of selectionMode='single' + disallowEmptySelection
            (dropdown.tsx:182-184) to match the buildSheetMenuContent pattern (dropdown.tsx:517-520). */}
        <Dropdown.Menu selectionMode='none'>
          {items.map(item => (
            <Dropdown.Item
              key={item.href}
              id={item.href}
              href={item.href}
              textValue={item.label}
              label={item.label}
              icon={item.icon}
              {...(item.onPrefetch || {})}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
};
